import {
  ETIQUETA_ESTADO_BB,
  ETIQUETA_ESTADO_BB_EN,
  ETIQUETA_ORIGEN_CAMBIO,
  ETIQUETA_ORIGEN_CAMBIO_EN,
  ETIQUETA_ORIGEN_LOTE,
  ETIQUETA_ORIGEN_LOTE_EN,
  ETIQUETA_TIPO_EXPORTACION,
  ETIQUETA_TIPO_EXPORTACION_EN,
  type ExportacionDetalle,
} from '@cnc/contracts';
import {useState} from 'react';
import {Link, useParams} from 'react-router';
import {ErrorDeApi} from '../api/cliente';
import {useRevisarExportacion} from '../api/navieras';
import {useExportacion} from '../api/seguimiento';
import {AsignacionDeContenedores} from '../componentes/asignacion-de-contenedores';
import {EstadoExportacion} from '../componentes/estado-exportacion';
import {DocumentosDeLaExportacion} from '../componentes/archivos-de-exportacion';
import {HistorialDeCambios} from '../componentes/historial-de-cambios';
import {LogisticaDeExportacion} from '../componentes/logistica-de-exportacion';
import {AlertasDeExportacion} from '../componentes/alertas-de-exportacion';
import {PaqueteDocumental} from '../componentes/paquete-documental';
import {CartaDeInstruccion} from '../componentes/carta-de-instruccion';
import {SepararExportacion} from '../componentes/separar-exportacion';
import {TramiteDeAduana} from '../componentes/tramite-de-aduana';
import {FinanciacionDeExportacion} from '../componentes/financiacion-de-exportacion';
import {AYUDA_DETALLE} from '../componentes/ayuda-exportacion';
import {AYUDA_SEGUIMIENTO} from '../componentes/ayuda-seguimiento';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {SiVe} from '../componentes/con-area';
import {useNivel} from '../componentes/sesion-contexto';
import {et, tr, type Texto} from '../i18n/idioma';
import {fecha, fechaHora, numero, textoOGuion} from '../utilidades/formato';

export function PaginaExportacion() {
  const {codigo = ''} = useParams();
  const consulta = useExportacion(codigo);

  return (
    <section className="max-w-6xl">
      <Link to="/seguimiento" className="text-sm text-verde hover:underline">
        {tr('← Volver al seguimiento', '← Back to tracking')}
      </Link>
      {consulta.isPending && <p className="mt-4 text-apagado">{tr('Consultando…', 'Loading…')}</p>}
      {consulta.isError && (
        <div className="mt-4 rounded-lg border border-alerta/30 bg-red-50 p-4 text-sm">
          <div className="font-medium text-alerta">
            {tr('No se pudo consultar la exportación', 'Could not load the export')}
          </div>
          <div className="mt-1">{consulta.error.message}</div>
        </div>
      )}
      {consulta.data && <Detalle x={consulta.data} />}
    </section>
  );
}

function Detalle({x}: {x: ExportacionDetalle}) {
  const editaSeguimiento = useNivel('exportaciones.seguimiento') === 'editar';
  const consultarNaviera = useRevisarExportacion();
  const [avisoNaviera, setAvisoNaviera] = useState<string | null>(null);
  const [separando, setSeparando] = useState(false);
  const consultar = async () => {
    setAvisoNaviera(null);
    try {
      const r = await consultarNaviera.mutateAsync(x.codigo);
      setAvisoNaviera(
        r.cambios.length
          ? `${r.mensaje ?? ''} ${r.cambios.map(c => `${c.etiqueta}: ${c.antes ?? '—'} → ${c.despues ?? '—'}`).join('; ')}`
          : (r.mensaje ?? tr('Sin novedades.', 'No news.')),
      );
    } catch (error) {
      setAvisoNaviera(
        error instanceof ErrorDeApi
          ? error.message
          : tr('No se pudo consultar a la naviera.', 'Could not reach the shipping line.'),
      );
    }
  };
  const A = AYUDA_SEGUIMIENTO;
  // De donde salio cada fecha (decision 0066): reserva, rastreo, naviera o una persona.
  const o = (campo: string, valor: string) => {
    const origen = x.origenDeFechas[campo];
    return origen ? `${valor} · ${origen.etiqueta}, ${fecha(origen.el.slice(0, 10))}` : valor;
  };
  const ruta: Array<Dato> = [
    [tr('Puerto de carga', 'Port of loading'), textoOGuion(x.puertoCarga), A.puertoCarga],
    [tr('Destino', 'Destination'), textoOGuion(x.puertoDestino), A.puertoDestino],
    [tr('Naviera', 'Shipping line'), textoOGuion(x.naviera), A.naviera],
    [tr('Buque', 'Vessel'), textoOGuion(x.buque), A.buque],
    ['Booking', textoOGuion(x.booking), A.booking],
    [tr('Agente', 'Agent'), textoOGuion(x.agente), A.agente],
    ['ETD', o('etd', fecha(x.etd)), A.etd],
    [tr('ETA destino', 'ETA destination'), o('eta', fecha(x.eta)), A.eta],
    ['ETA Mundra', o('etaMundra', fecha(x.etaMundra)), A.etaMundra],
    ['ETA Sachana', o('etaSachana', fecha(x.etaSachana)), A.etaSachana],
    ['ETA Hindustan', o('etaHindustan', fecha(x.etaHindustan)), A.etaHindustan],
    [tr('ETA inicial', 'Initial ETA'), o('etaInicial', fecha(x.etaInicial)), A.etaInicial],
    [tr('Zarpe real', 'Actual departure'), o('fechaZarpe', fecha(x.fechaZarpe)), A.fechaZarpe],
    [tr('Llegada real', 'Actual arrival'), o('fechaLlegada', fecha(x.fechaLlegada)), A.fechaLlegada],
    [tr('Fecha en tren', 'Rail date'), o('fechaTren', fecha(x.fechaTren)), A.fechaTren],
    [tr('Puerto actual', 'Current port'), x.puertoActual ?? '—', A.puertoActual],
    [tr('ETD puerto actual', 'Current port ETD'), o('etdPuertoActual', fecha(x.etdPuertoActual)), A.etdPuertoActual],
    [
      tr('Llegada a Hindustan', 'Arrival at Hindustan'),
      o('fechaLlegadaHindustan', fecha(x.fechaLlegadaHindustan)),
      A.fechaLlegadaHindustan,
    ],
    [tr('Fecha del BL', 'BL date'), fecha(x.fechaBl), A.fechaBl],
    [tr('Liquidación', 'Settlement'), fecha(x.fechaLiquidacion), A.fechaLiquidacion],
    [tr('Días libres', 'Free days'), o('diasLibres', numero(x.diasLibres)), A.diasLibres],
    [tr('Días en tránsito', 'Days in transit'), numero(x.diasEnTransito), A.diasEnTransito],
    [
      tr('Días en tránsito planeados', 'Planned days in transit'),
      numero(x.diasEnTransitoInicial),
      A.diasEnTransitoInicial,
    ],
    [
      tr('Última ubicación (naviera)', 'Last location (shipping line)'),
      x.ultimoEvento
        ? tr(
            `${x.ultimoEvento.tipo} en ${x.ultimoEvento.lugar ?? 'sin nombre'}, ${fecha(x.ultimoEvento.fecha.slice(0, 10))}`,
            `${x.ultimoEvento.tipo} at ${x.ultimoEvento.lugar ?? 'unnamed place'}, ${fecha(x.ultimoEvento.fecha.slice(0, 10))}`,
          )
        : '—',
      A.ultimoEvento,
    ],
    [
      tr('Consultada a la naviera', 'Checked with the shipping line'),
      x.navieraConsultadaEl ? fechaHora(x.navieraConsultadaEl) : tr('nunca', 'never'),
      A.navieraConsultadaEl,
    ],
  ];
  const comercial: Array<Dato> = [
    [tr('Comprador', 'Buyer'), textoOGuion(x.comprador), A.comprador],
    ['Incoterm', textoOGuion(x.incoterm), A.incoterm],
    [tr('Valor aproximado (USD)', 'Approximate value (USD)'), numero(x.valorAproximadoUsd, 2), A.valorAproximadoUsd],
    [tr('Empresa que factura', 'Invoicing company'), textoOGuion(x.facturadora), A.facturadora],
    ['XAU', numero(x.xau, 2), A.xau],
    ['XAG', numero(x.xag, 2), A.xag],
    ['LBMA', fecha(x.fechaLbma), A.fechaLbma],
    [tr('Financiada por BLQ', 'Financed by BLQ'), textoOGuion(x.financiadaPorBlq), A.financiadaPorBlq],
    [tr('Notas', 'Notes'), textoOGuion(x.notas), A.notas],
  ];
  const adicionales = Object.entries(x.extra)
    .filter(([, v]) => v !== null && v !== '')
    .map(([k, v]) => [k, String(v)] as Dato);
  const originales = Object.entries(x.valoresOriginales).map(([k, v]) => [k, textoOGuion(v)] as Dato);
  const D = AYUDA_DETALLE;
  const enCurso = !x.estadoFinal && x.avance !== null;

  return (
    <div className="mt-3 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold text-verde-oscuro">
          {tr('Exportación', 'Export')} {x.codigo}
        </h1>
        <span>
          <EstadoExportacion estado={x.estado} etiqueta={x.estadoEtiqueta} final={x.estadoFinal} />
          <NotaDeAyuda titulo={tr('Estado', 'Status')} texto={A.estado} />
        </span>
        <span className="text-sm text-apagado">
          {x.exportador ?? tr('Sin exportador', 'No exporter')}
          <NotaDeAyuda titulo={tr('Exportador', 'Exporter')} texto={A.exportador} /> ·{' '}
          {et(ETIQUETA_TIPO_EXPORTACION, ETIQUETA_TIPO_EXPORTACION_EN)[x.tipo]}
          <NotaDeAyuda titulo={tr('Tipo', 'Type')} texto={A.tipo} />
        </span>
        <SiVe area="exportaciones.contenedores">
          <a href="#logistica" className="text-sm text-verde hover:underline">
            {tr('Logística por contenedor ↓', 'Container logistics ↓')}
          </a>
        </SiVe>
        <SiVe area="exportaciones.gex">
          <a href="#aduana" className="text-sm text-verde hover:underline">
            {tr('Trámite de aduana ↓', 'Customs filing ↓')}
          </a>
        </SiVe>
        <SiVe area="exportaciones.financiaciones">
          <a href="#financiacion" className="text-sm text-verde hover:underline">
            {tr('Financiación ↓', 'Financing ↓')}
          </a>
        </SiVe>
        {x.enlaceNaviera && (
          <a href={x.enlaceNaviera} target="_blank" rel="noreferrer" className="text-sm text-verde hover:underline">
            {tr('Rastreo de la naviera ↗', 'Shipping line tracking ↗')}
          </a>
        )}
        <div className="ml-auto flex items-center gap-2">
          {editaSeguimiento && x.booking && (
            <button
              type="button"
              onClick={() => void consultar()}
              disabled={consultarNaviera.isPending}
              className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel disabled:opacity-50"
            >
              {consultarNaviera.isPending
                ? tr('Consultando…', 'Checking…')
                : tr('⚓ Consultar naviera', '⚓ Check shipping line')}
            </button>
          )}
          {editaSeguimiento && x.tipo !== 'terceros' && (
            <button
              type="button"
              onClick={() => setSeparando(v => !v)}
              aria-expanded={separando}
              className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel"
            >
              {tr('✂ Separar en A y B', '✂ Split into A and B')}
            </button>
          )}
          {editaSeguimiento && (
            <Link
              to={`/seguimiento/${encodeURIComponent(x.codigo)}/editar`}
              className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel"
            >
              {tr('✎ Editar', '✎ Edit')}
            </Link>
          )}
        </div>
      </div>
      {separando && <SepararExportacion codigo={x.codigo} onCerrar={() => setSeparando(false)} />}
      {avisoNaviera && <div className="rounded-lg border border-linea bg-papel p-3 text-sm">{avisoNaviera}</div>}

      {/* Lo general de la exportacion es del Seguimiento; cada seccion, de su
          pestana (decision 0062). */}
      <SiVe area="exportaciones.seguimiento">
        {enCurso && (
          <div className="rounded-lg border border-linea bg-white p-4">
            <div className="flex justify-between text-xs text-apagado">
              <span>
                {tr('Zarpe', 'Departure')} {fecha(x.etd)}
              </span>
              <span>
                {tr('Avance estimado', 'Estimated progress')}
                <NotaDeAyuda titulo={tr('Avance estimado', 'Estimated progress')} texto={A.avance} />{' '}
                {numero((x.avance ?? 0) * 100)} % ·{' '}
                {x.diasParaLlegada === null
                  ? tr('sin ETA', 'no ETA')
                  : x.diasParaLlegada < 0
                    ? tr(
                        `ETA vencida hace ${numero(-x.diasParaLlegada)} días`,
                        `ETA overdue by ${numero(-x.diasParaLlegada)} days`,
                      )
                    : x.diasParaLlegada === 0
                      ? tr('llega hoy', 'arrives today')
                      : tr(`faltan ${numero(x.diasParaLlegada)} días`, `${numero(x.diasParaLlegada)} days to go`)}
              </span>
              <span>
                {tr('Llegada', 'Arrival')} {fecha(x.eta)}
              </span>
            </div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-papel">
              <div className="h-full rounded-full bg-verde" style={{width: `${(x.avance ?? 0) * 100}%`}} />
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
          <Indicador
            titulo="Big Bags"
            valor={numero(x.bigBags)}
            detalle={
              x.diferenciaBigBags
                ? tr(
                    `⚠ el seguimiento reporta ${numero(x.bigBagsReportados)}`,
                    `⚠ tracking reports ${numero(x.bigBagsReportados)}`,
                  )
                : x.fuenteDeTotales === 'bolsas'
                  ? tr('contadas de las bolsas', 'counted from the bags')
                  : tr('reportadas en el seguimiento', 'reported in tracking')
            }
            ayuda={A.bigBags}
          />
          <Indicador titulo={tr('Contenedores', 'Containers')} valor={numero(x.contenedores)} ayuda={A.contenedores} />
          <Indicador titulo="WMT" valor={numero(x.tonHumedas, 3)} ayuda={A.tonHumedas} />
          <Indicador titulo="DMT" valor={numero(x.tonSecas, 3)} ayuda={A.tonSecas} />
          <Indicador titulo="Au (oz)" valor={numero(x.auOz, 2)} detalle={`${numero(x.auG, 2)} g`} ayuda={A.auOz} />
          <Indicador titulo="Ag (oz)" valor={numero(x.agOz, 2)} detalle={`${numero(x.agG, 2)} g`} ayuda={A.agOz} />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Tarjeta titulo={tr('Ruta y fechas', 'Route and dates')} datos={ruta} />
          <div className="space-y-4">
            <Tarjeta titulo={tr('Comercial', 'Commercial')} datos={comercial} />
            <TarjetaLibre titulo={tr('Facturas', 'Invoices')}>
              {x.facturas.length === 0 ? (
                <p className="text-sm text-apagado">{tr('Sin facturas registradas.', 'No invoices recorded.')}</p>
              ) : (
                <TablaSencilla
                  encabezados={[
                    tr('Número', 'Number'),
                    tr('Fecha', 'Date'),
                    tr('Año', 'Year'),
                    tr('Mina', 'Mine'),
                    tr('Monto (USD)', 'Amount (USD)'),
                  ]}
                  ayudas={[D.facturaNumero, D.facturaFecha, D.facturaAnio, D.facturaMina, D.facturaMonto]}
                  filas={x.facturas.map(f => [
                    textoOGuion(f.numero),
                    fecha(f.fecha),
                    textoOGuion(f.anio),
                    textoOGuion(f.mina),
                    numero(f.montoUsd, 2),
                  ])}
                  numericas={[4]}
                />
              )}
            </TarjetaLibre>
          </div>
        </div>

        <TarjetaLibre
          titulo={`${tr('Contenedores', 'Containers')} (${numero(x.listaDeContenedores.length)})`}
          nota={tr(
            'Cada contenedor lleva hasta 28 Big Bags y 28 toneladas húmedas.',
            'Each container holds up to 28 Big Bags and 28 wet tonnes.',
          )}
        >
          {x.listaDeContenedores.length === 0 ? (
            <p className="text-sm text-apagado">{tr('Sin contenedores registrados.', 'No containers recorded.')}</p>
          ) : (
            <TablaSencilla
              encabezados={[tr('Contenedor', 'Container'), tr('Orden', 'Order'), 'Big Bags', 'WMT', 'DMT']}
              ayudas={[D.contenedorNumero, D.contenedorOrden, D.contenedorBigBags, D.contenedorWmt, D.contenedorDmt]}
              filas={x.listaDeContenedores.map(c => [
                c.numero,
                textoOGuion(c.secuencia),
                numero(c.bigBags),
                numero(c.tonHumedas, 3),
                numero(c.tonSecas, 3),
              ])}
              numericas={[1, 2, 3, 4]}
            />
          )}
        </TarjetaLibre>
      </SiVe>

      <SiVe area="exportaciones.contenedores">
        <LogisticaDeExportacion codigo={x.codigo} />
      </SiVe>
      <SiVe area="exportaciones.gex">
        <TramiteDeAduana codigo={x.codigo} />
      </SiVe>
      <SiVe area="exportaciones.financiaciones">
        <FinanciacionDeExportacion codigo={x.codigo} />
      </SiVe>

      <SiVe area="exportaciones.seguimiento">
        <TarjetaLibre
          titulo={`${tr('Big Bags incluidos', 'Big Bags included')} (${numero(x.listaDeBigBags.length)})`}
          enlace={
            x.listaDeBigBags.length > 0
              ? {
                  a: `/inventario?exportacion=${encodeURIComponent(x.codigo)}`,
                  texto: tr('Ver en el inventario', 'View in inventory'),
                }
              : undefined
          }
        >
          {x.listaDeBigBags.length === 0 ? (
            <p className="text-sm text-apagado">
              {tr(
                'Esta exportación no tiene Big Bags en el inventario; sus totales son los reportados en el seguimiento.',
                'This export has no Big Bags in inventory; its totals are the ones reported in tracking.',
              )}
            </p>
          ) : (
            <div className="max-h-96 overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="text-left text-xs uppercase tracking-wide text-apagado">
                    <th className="py-1 pr-2 text-right font-semibold" title={tr('Número de línea', 'Line number')}>
                      #
                    </th>
                    <th className="py-1 font-semibold">
                      BB #<NotaDeAyuda titulo="BB #" texto={D.bbNo} />
                    </th>
                    <th className="py-1 font-semibold">
                      {tr('Estado', 'Status')}
                      <NotaDeAyuda titulo={tr('Estado', 'Status')} texto={D.bbEstado} />
                    </th>
                    <th className="py-1 font-semibold">
                      {tr('Proveedor', 'Supplier')}
                      <NotaDeAyuda titulo={tr('Proveedor', 'Supplier')} texto={D.bbProveedor} />
                    </th>
                    <th className="py-1 font-semibold">
                      {tr('Mina', 'Mine')}
                      <NotaDeAyuda titulo={tr('Mina', 'Mine')} texto={D.bbMina} />
                    </th>
                    <th className="py-1 font-semibold">
                      {tr('Contenedor', 'Container')}
                      <NotaDeAyuda titulo={tr('Contenedor', 'Container')} texto={D.bbContenedor} />
                    </th>
                    <th className="py-1 font-semibold">
                      {tr('Sello', 'Seal')}
                      <NotaDeAyuda titulo={tr('Sello', 'Seal')} texto={D.bbSello} />
                    </th>
                    <th className="py-1 text-right font-semibold">
                      WMT
                      <NotaDeAyuda titulo="WMT" texto={D.bbWmt} />
                    </th>
                    <th className="py-1 text-right font-semibold">
                      DMT
                      <NotaDeAyuda titulo="DMT" texto={D.bbDmt} />
                    </th>
                    <th className="py-1 text-right font-semibold">
                      Au (g)
                      <NotaDeAyuda titulo="Au (g)" texto={D.bbAuG} />
                    </th>
                    <th className="py-1 text-right font-semibold">
                      Ag (g)
                      <NotaDeAyuda titulo="Ag (g)" texto={D.bbAgG} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {x.listaDeBigBags.map((b, i) => (
                    <tr key={b.bbNo} className="border-t border-linea/70">
                      <td className="py-1 pr-2 text-right text-xs tabular-nums text-apagado" data-linea={i + 1}>
                        {i + 1}
                      </td>
                      <td className="py-1">
                        <Link
                          to={`/inventario/${encodeURIComponent(b.bbNo)}`}
                          className="font-medium text-verde hover:underline"
                        >
                          {b.bbNo}
                        </Link>
                      </td>
                      <td className="py-1">{et(ETIQUETA_ESTADO_BB, ETIQUETA_ESTADO_BB_EN)[b.estado]}</td>
                      <td className="py-1">{textoOGuion(b.proveedor)}</td>
                      <td className="py-1">{textoOGuion(b.mina)}</td>
                      <td className="py-1">{textoOGuion(b.contenedor)}</td>
                      <td className="py-1">{textoOGuion(b.sello)}</td>
                      <td className="py-1 text-right tabular-nums">{numero(b.tonHumedas, 3)}</td>
                      <td className="py-1 text-right tabular-nums">{numero(b.tonSecas, 3)}</td>
                      <td className="py-1 text-right tabular-nums">{numero(b.auG, 2)}</td>
                      <td className="py-1 text-right tabular-nums">{numero(b.agG, 2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TarjetaLibre>
      </SiVe>

      {x.tipo === 'propia' && (
        <SiVe area="exportaciones.reservas">
          <AsignacionDeContenedores codigo={x.codigo} />
        </SiVe>
      )}

      {x.tipo === 'propia' && (
        <SiVe area="exportaciones.documentos">
          <PaqueteDocumental codigo={x.codigo} />
        </SiVe>
      )}

      {/* Archivos de Dropbox y expediente en una sola vista (decision 0092). */}
      <SiVe area="exportaciones.documentos">
        <DocumentosDeLaExportacion codigo={x.codigo} documentos={x.documentos} />
      </SiVe>

      {/* La carta de instruccion a Brink's (SLI) va en todas: MM, CM, MA e IPM (24/10/2026). */}
      <SiVe area="exportaciones.documentos">
        <CartaDeInstruccion codigo={x.codigo} />
      </SiVe>

      <SiVe area="exportaciones.seguimiento">
        <AlertasDeExportacion codigo={x.codigo} alertas={x.alertas} />
      </SiVe>

      <SiVe area="exportaciones.seguimiento">
        <TarjetaLibre
          titulo={`${tr('Historial de cambios', 'Change history')} (${numero(x.cambios.length)})`}
          nota={tr(
            'Los últimos cambios, uno por línea; clic en uno muestra todos sus datos.',
            'The latest changes, one per line; click one to see all its data.',
          )}
        >
          <HistorialDeCambios cambios={x.cambios} origenes={origenDeCambio()} />
        </TarjetaLibre>

        <div className="grid gap-4 md:grid-cols-2">
          <TarjetaLibre titulo={tr('Historial de fechas (ETD y ETA)', 'Date history (ETD and ETA)')}>
            {x.historialEta.length === 0 ? (
              <p className="text-sm text-apagado">{tr('Sin cambios registrados.', 'No changes recorded.')}</p>
            ) : (
              <TablaSencilla
                encabezados={[
                  tr('Fecha del cambio', 'Change date'),
                  tr('Campo', 'Field'),
                  tr('Antes', 'Before'),
                  tr('Después', 'After'),
                  tr('Origen', 'Source'),
                ]}
                ayudas={[D.historialFecha, D.historialCampo, D.historialAntes, D.historialDespues, D.historialOrigen]}
                filas={x.historialEta.map(h => [
                  fecha(h.cambiadoEl.slice(0, 10)),
                  h.campoEtiqueta,
                  fecha(h.anterior),
                  fecha(h.nuevo),
                  et(ETIQUETA_ORIGEN_CAMBIO, ETIQUETA_ORIGEN_CAMBIO_EN)[h.origen],
                ])}
              />
            )}
          </TarjetaLibre>
          <div className="space-y-4">
            {originales.length > 0 && (
              <Tarjeta
                titulo={tr('Valores del archivo que se normalizaron', 'File values that were normalized')}
                ayuda={D.valoresOriginales}
                datos={originales}
                nota={tr(
                  'Lo que traía el archivo de seguimiento antes de limpiarlo (por ejemplo N/A o fechas de 1900).',
                  'What the tracking file contained before it was cleaned up (for example N/A or 1900 dates).',
                )}
              />
            )}
            {adicionales.length > 0 && (
              <Tarjeta
                titulo={tr('Datos adicionales del archivo', 'Additional data from the file')}
                ayuda={D.adicionales}
                datos={adicionales}
              />
            )}
          </div>
        </div>
      </SiVe>
    </div>
  );
}

const origenDeCambio = (): Record<string, string> => ({
  ...et(ETIQUETA_ORIGEN_LOTE, ETIQUETA_ORIGEN_LOTE_EN),
  naviera: tr('Página de la naviera', 'Shipping line website'),
  carga: tr('Carga de archivo', 'File upload'),
  sistema: tr('Sistema', 'System'),
});

// Un dato de una tarjeta: nombre, valor y (si la hay) su nota de ayuda.
type Dato = [string, string] | [string, string, Texto | undefined];

function Indicador({titulo, valor, detalle, ayuda}: {titulo: string; valor: string; detalle?: string; ayuda?: Texto}) {
  return (
    <div className="rounded-lg border border-linea bg-white px-3 py-2">
      <div className="text-xs text-apagado">
        {titulo}
        {ayuda && <NotaDeAyuda titulo={titulo} texto={ayuda} />}
      </div>
      <div className="text-base font-semibold tabular-nums text-verde-oscuro">{valor}</div>
      {detalle && <div className="text-xs text-apagado">{detalle}</div>}
    </div>
  );
}

function Tarjeta({
  titulo,
  datos,
  nota,
  ayuda,
}: {
  titulo: string;
  datos: Array<Dato>;
  nota?: string;
  // Nota comun a todos los datos de la tarjeta (los que no traen la suya).
  ayuda?: Texto;
}) {
  return (
    <div className="rounded-lg border border-linea bg-white p-5">
      <h2 className="font-medium">{titulo}</h2>
      {nota && <p className="mt-1 text-xs text-apagado">{nota}</p>}
      <dl className="mt-3 space-y-2">
        {datos.map(([nombre, valor, propia = ayuda]) => (
          <div key={nombre} className="flex justify-between gap-4 text-sm">
            <dt className="text-apagado">
              {nombre}
              {propia && <NotaDeAyuda titulo={nombre} texto={propia} />}
            </dt>
            <dd className="text-right tabular-nums">{valor}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function TarjetaLibre({
  titulo,
  nota,
  enlace,
  children,
}: {
  titulo: string;
  nota?: string;
  enlace?: {a: string; texto: string};
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-linea bg-white p-5">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="font-medium">{titulo}</h2>
        {enlace && (
          <Link to={enlace.a} className="text-xs text-verde hover:underline">
            {enlace.texto}
          </Link>
        )}
      </div>
      {nota && <p className="mt-1 text-xs text-apagado">{nota}</p>}
      <div className="mt-3">{children}</div>
    </div>
  );
}

function TablaSencilla({
  encabezados,
  filas,
  numericas = [],
  ayudas = [],
}: {
  encabezados: string[];
  filas: string[][];
  numericas?: number[];
  // Nota de ayuda de cada encabezado, en el mismo orden.
  ayudas?: Array<Texto | undefined>;
}) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs uppercase tracking-wide text-apagado">
          {encabezados.map((e, i) => (
            <th key={e} className={`py-1 font-semibold ${numericas.includes(i) ? 'text-right' : ''}`}>
              {e}
              {ayudas[i] && <NotaDeAyuda titulo={e} texto={ayudas[i]} />}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {filas.map((fila, indice) => (
          <tr key={indice} className="border-t border-linea/70">
            {fila.map((celda, i) => (
              <td key={i} className={`py-1 ${numericas.includes(i) ? 'text-right tabular-nums' : ''}`}>
                {celda}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
